import classnames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { motionFadeIn } from '@apps-common/config/motion-variants'

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  size?: 'sm' | 'lg'
  loading?: boolean
  disabled?: boolean
}

export const TextAreaSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>((props: InputProps, ref) => {
  const {
    className,
    containerClassName,
    registration,
    type = 'text',
    placeholder,
    autoComplete = 'off',
    id,
    showLabel = false,
    label,
    errors,
    loading = false,
    disabled = false,
    children
  } = props

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-text-area-sm-input-skeleton`}
          {...motionFadeIn(0.25)}
          className={classnames('mb-2 animate-pulse', containerClassName)}
        >
          <div className={classnames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />

          <div className='relative'>
            <div className={classnames('bg-skeleton py-[1rem] w-full min-h-[100px] rounded-[20px] animate-pulse', className)} />
            {children}
          </div>
          <p className='pl-2 pt-2 h-[5px]'>{errors?.message}</p>
        </motion.div>
      )}
      {!loading && (
        <div key={`${id}-text-area-sm-input`} {...motionFadeIn(0.25)} className={classnames('mb-2', containerClassName)}>
          <label className={classnames('block pl-2 pb-2 text-sm text-text-muted font-bold', { 'sr-only': !showLabel })} htmlFor={id}>
            {label}
          </label>
          <div className='relative'>
            <textarea
              id={id}
              placeholder={placeholder}
              className={classnames(
                'input-bordered border-border bg-surface disabled:bg-surface-2 py-[1rem] text-text placeholder:text-text-muted/60 input w-full text-sm min-h-[100px] rounded-[20px]',
                { 'input-error': errors },
                className
              )}
              disabled={disabled}
              autoComplete={autoComplete}
              {...registration}
            />
            {children}
          </div>
          <p className='pl-2 pt-2 text-error text-xs'>{errors?.message}</p>
        </div>
      )}
    </AnimatePresence>
  )
})

TextAreaSM.displayName = 'Input'
